// R8-5 — THE SEAL LALA HANDS OVER IN CHAT, LIFTED OUT OF App.tsx VERBATIM.
//
// Props in, tree out, no state, no hook — the entry requirement. This is the surface a member sees
// when a working is answered with a seal, and the whole seal architecture upstream of it (the work
// order, the contract, the repair ladder) exists so that what arrives here is correct. Nothing had
// ever checked that this card SHOWS what arrived: that the image is the auth-gated seal route, that
// the seal is named and not merely pictured, that the verse renders as its own block with its
// reference, and that the download saves under the seal's own slug.
//
// The other half is the empty case, and it is the one worth a gate: a bare seal (no meaning, no
// verse, no ways to use — a real delivery) must render the seal and OMIT the empty blocks, never
// draw a "Ways to use" heading over nothing.
//
// The `DeliveredSeal` type comes with it, the way R7 moved `Generator` with its catalog and R8-3
// moved `LalaAttachment`: the shape belongs to the thing that renders it.
//
// Moved VERBATIM.
//
// >>> IT #140 (2026-08-21) — "SEALS NOT ARRIVING OR DOWNLOADING IN THE CHAT CONVERSATION." <<<
// The download here was a bare `<a href={seal.imageUrl} download>` on an AUTH-GATED route that
// composes 401, 404 and 500 in its own words — and **an anchor cannot read a refusal** (R101/R88),
// so a refused download either navigated her to a JSON error page or did nothing at all, with
// nothing anywhere telling her which. That is #127's own repair, which was applied to MY ROOM's door
// and never to this one — its build note says in as many words that both controls go through
// `downloadFromUrl` *"which reads the ANSWER"*.
//
// **The `onDownload` handler is a PROP rather than an import**, because this component's entry
// requirement is props-in-tree-out with no state and no hook: a gate CALLS it and reads the tree
// back, and an imported transport would make the refusal path unreachable to it (R46's locked-door
// shape). The call site hands over `downloadFromUrl`.
import { Download } from "lucide-react";

export type DeliveredSeal = {
  slug: string;
  name: string;
  category: string;
  planet?: string;
  meaning?: string;
  verseRef?: string;
  verseText?: string;
  waysToUse?: string[];
  imageUrl: string;
};

// A seal Lala hands over in the chat: shown reverently, with its guidance and a
// download. The image is served from the auth-gated /api/seals/:slug/image route.
export function SealCard({ seal, onDownload }: { seal: DeliveredSeal; onDownload?: (url: string) => void }) {
  return (
    <figure className="seal-card">
      <p className="seal-card__eyebrow">A seal for you</p>
      <img className="seal-card__image" src={seal.imageUrl} alt={seal.name} loading="lazy" />
      <figcaption className="seal-card__body">
        <h3 className="seal-card__name">{seal.name}</h3>
        {seal.meaning ? <p className="seal-card__meaning">{seal.meaning}</p> : null}
        {seal.verseText ? (
          <p className="seal-card__verse">
            {seal.verseRef ? <span className="seal-card__verse-ref">{seal.verseRef}</span> : null}
            <span>&ldquo;{seal.verseText}&rdquo;</span>
          </p>
        ) : null}
        {seal.waysToUse && seal.waysToUse.length ? (
          <div className="seal-card__ways">
            <span className="seal-card__ways-title">Ways to use</span>
            <ul>
              {seal.waysToUse.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {/* >>> A BUTTON RATHER THAN AN ANCHOR, WHICH IS THE WHOLE REPAIR (R101/R88/#127). <<< The
            route composes its own 401, 404 and 500 and an anchor cannot read any of them; the
            transport does, and the SERVER names the file through `Content-Disposition` — without
            that header this would have saved her seal as `12thHouse.pdf`, which is a repair that
            makes the download worse. `onDownload` is optional so the component stays callable by a
            gate with no transport at all, and with none it does nothing rather than falling back to
            the blind anchor this round is removing (R165: a retired path left beside its replacement
            is one import away from a round that finds it and assumes it is wanted). */}
        <button
          type="button"
          className="seal-card__download"
          onClick={() => onDownload?.(seal.imageUrl)}
        >
          <Download size={15} /> Download seal
        </button>
      </figcaption>
    </figure>
  );
}
